import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import ShortAnswer from "@/components/ShortAnswer";
import { HUB_PATHS, EMPLOYEE_GUIDES, FREE_HELP, CHECKED_ON } from "@/data/equality";
import { Hero, GuideList, CheckerCard, breadcrumb, linkClass } from "./shared";

export default function EmployeesHub() {
  const origin = window.location.origin;
  const pageUrl = `${origin}${HUB_PATHS.employees}`;
  return (
    <main data-testid="equality-employees-hub">
      <Seo
        title="Your Rights at Work: Equality Guides for Employees (UK) | Electra HR"
        siteName="Electra HR"
        description="Free plain-English guides to your rights at work in the UK, especially for women: pregnancy and maternity, mental health, neurodivergence, menopause, flexible working, equal pay and harassment."
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": pageUrl,
              url: pageUrl,
              name: "Your rights at work",
              isPartOf: { "@id": `${origin}/#website` },
              publisher: { "@id": `${origin}/#organization` },
              dateModified: CHECKED_ON.iso,
            },
            breadcrumb(origin, [
              { name: "Home", path: "/" },
              { name: "Equality at work", path: HUB_PATHS.home },
              { name: "For people at work", path: HUB_PATHS.employees },
            ]),
          ],
        }}
      />

      <Hero
        testid="employees-hero"
        eyebrow="Equality at work · For people at work"
        title="Know where you stand."
        accent="Then ask for what you need."
        intro="Plain-English guides to your rights at work, with a focus on women at work: pregnancy, health, pay, flexibility and harassment. Written by a solicitor, free, with no sign-up."
      />

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x max-w-5xl">
          <div data-testid="employees-positioning" className="mb-10 border-2 border-ink/15 bg-white p-5 text-sm leading-relaxed text-ink/70">
            Electra HR advises employers. This hub is free information for anyone at work, so that both sides start from the same facts. It is not advice on your own situation: for that, use the free services listed below.
          </div>

          <ShortAnswer path={HUB_PATHS.employees} className="mb-12" />

          <CheckerCard to={HUB_PATHS.checker} testid="employees-checker-card" />

          <h2 className="mt-16 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Guides for people at work</h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink/70">
            Each guide has a twin in the <Link to={HUB_PATHS.employers} className={linkClass}>employers' hub</Link>, so you can see what your employer is being told too.
          </p>
          <div className="mt-8">
            <GuideList guides={EMPLOYEE_GUIDES} testid="employee-guides" />
          </div>

          <h2 className="mt-16 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Watch the time limit</h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink/70">
            If something has gone wrong, most employment tribunal claims, including discrimination, must be started within six months less one day of what happened, where it happened on or after 1 October 2026. Before that date the limit was three months less one day. You must normally contact Acas for early conciliation first, which can extend the deadline. Some claims have shorter limits. Get advice early; don't wait for an internal process to finish.
          </p>
          <p className="mt-3 text-xs text-ink/55">
            Law:{" "}
            <a href="https://www.legislation.gov.uk/ukpga/2010/15/section/123" target="_blank" rel="noopener noreferrer" className="underline decoration-pink underline-offset-2 hover:text-ink">Equality Act 2010, s.123</a>;{" "}
            <a href="https://www.legislation.gov.uk/ukpga/2025/36/schedule/12" target="_blank" rel="noopener noreferrer" className="underline decoration-pink underline-offset-2 hover:text-ink">Employment Rights Act 2025, Sch 12</a>;{" "}
            <a href="https://www.legislation.gov.uk/ukpga/1996/17/section/18A" target="_blank" rel="noopener noreferrer" className="underline decoration-pink underline-offset-2 hover:text-ink">Employment Tribunals Act 1996, s.18A</a>;{" "}
            <a href="https://www.legislation.gov.uk/ukpga/2010/15/section/140B" target="_blank" rel="noopener noreferrer" className="underline decoration-pink underline-offset-2 hover:text-ink">Equality Act 2010, s.140B</a>.
          </p>

          <h2 className="mt-16 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Free, independent help</h2>
          <ul data-testid="free-help" className="mt-8 grid gap-4 md:grid-cols-2">
            {FREE_HELP.map((h) => (
              <li key={h.name}>
                <a href={h.url} target="_blank" rel="noopener noreferrer" className="group block h-full border-2 border-ink bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]">
                  <p className="flex items-center gap-2 font-display text-xl font-semibold tracking-tight">
                    {h.name} <ExternalLink size={16} className="text-pink" />
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-ink/70">{h.what}</p>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
